import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

// Route mapping between EN and FR
export const routeMapping = {
  en: {
    home: '',
    house: 'house-of-fenicis',
    expertise: 'expertise',
    works: 'selected-works',
    founder: 'founder',
    contact: 'contact'
  },
  fr: {
    home: '',
    house: 'la-maison',
    expertise: 'expertises',
    works: 'references',
    founder: 'fondatrice',
    contact: 'contact'
  }
};

// Get the equivalent route in another language
export const getEquivalentRoute = (currentPath, targetLang) => {
  const currentLang = currentPath.startsWith('/fr') ? 'fr' : 'en';
  const pathWithoutLang = currentPath.replace(/^\/(en|fr)/, '').replace(/^\//, '');
  
  // Find which page key this path corresponds to
  let pageKey = 'home';
  for (const [key, value] of Object.entries(routeMapping[currentLang])) {
    if (value === pathWithoutLang) {
      pageKey = key;
      break;
    }
  }
  
  // Return the equivalent route in target language
  const targetRoute = routeMapping[targetLang][pageKey];
  return `/${targetLang}${targetRoute ? '/' + targetRoute : ''}`;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine language from URL
  const getLanguageFromPath = (path) => {
    if (path.startsWith('/fr')) return 'fr';
    return 'en';
  };
  
  const [language, setLanguage] = useState(() => getLanguageFromPath(location.pathname));
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false);
  
  // Update language when URL changes
  useEffect(() => {
    setLanguage(getLanguageFromPath(location.pathname));
  }, [location.pathname]);
  
  // Check browser language on first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('fenicis_visited');
    if (!hasVisited) {
      const browserLang = navigator.language.toLowerCase();
      const currentLang = getLanguageFromPath(location.pathname);
      
      // If browser is French but viewing English, or vice versa
      if (browserLang.startsWith('fr') && currentLang === 'en') {
        setShowLanguagePrompt(true);
      } else if (!browserLang.startsWith('fr') && currentLang === 'fr') {
        setShowLanguagePrompt(true);
      }
      
      localStorage.setItem('fenicis_visited', 'true');
    }
  }, [location.pathname]);
  
  const switchLanguage = (targetLang) => {
    const newPath = getEquivalentRoute(location.pathname, targetLang);
    navigate(newPath);
    setShowLanguagePrompt(false);
  };
  
  const dismissPrompt = () => {
    setShowLanguagePrompt(false);
  };
  
  return (
    <LanguageContext.Provider value={{ 
      language, 
      switchLanguage, 
      showLanguagePrompt, 
      dismissPrompt,
      t: (translations) => translations[language] || translations.en
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
